import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import FormInputWrapper from '../form-input-wrapper/FormInputWrapper.jsx';

import { addValidationField, removeValidationField } from '../redux/form/form.actions';

import { uuidv4, validateEmail } from '../_global/js/form.utils';

const integerRegEx = /^\d+$/;

const FormInput = ({
  groupId,
  inputType,
  inputLabel,
  inputDescriptionText,
  parentElementClass,
  inputName,
  inputRequired,
  sectionLabel,
  inputValidationLabel,
  inputValidationMessage,
  additionalInputs,
  inputTestData,
  autoPopulate,
  triggerValidation,
  formStepValidation,
  addValidationField,
  removeValidationField
  }) => {
  const inputId = useRef( uuidv4() );
  const [ inputValue, setInputValue ] = useState('');
  const [ showAdditional, setShowAdditional ] = useState(false);
  const [ isValid, setIsValid ] = useState(true);
  const [ requiresFormValidation, setRequiresFormValidation ] = useState(false);
  const validationLabel = inputValidationLabel || inputLabel;
  const errorMessage = inputValidationMessage || 'This field is required.';

  /**
   * When the component has been mounted
   */
  useEffect(() => {
    // Mark this field as required, as necessary
    if (inputRequired) {
      setRequiresFormValidation(true);
    }

    // Cleanup the component (unmount)
    return () => {
      // Remove this field from the invalid list
      removeValidationField(inputId.current);
    };
  }, []);

  /**
   * When the field validation requirement has changed
   */
  useEffect(() => {
    if (requiresFormValidation) {
      // Add this field to the form validation state
      addValidationField({
        id: inputId.current,
        label: validationLabel,
        sectionLabel: sectionLabel,
        required: inputRequired,
        formStep: formStepValidation
      });
    } else {
      // Remove this field from the form validation state
      removeValidationField(inputId.current);
    }
  }, [requiresFormValidation]);

  /**
   * When the component has been instructed to validate it's input
   */
  useEffect(() => {
    if (triggerValidation !== null) {

      // Check if this input is in the current form step
      if (triggerValidation.formStep === formStepValidation) {
        // Check if the input is valid
        const validationSuccess = inputIsValid(inputValue, inputType);

        // Field is required, but failed validation
        if (inputRequired && !validationSuccess) {
          // Display the error message
          setIsValid(false);

          // Mark this field as invalid
          setRequiresFormValidation(true);
        }
      }
    }
  }, [triggerValidation]);

  /**
   * When the component has been instructed to auto-populate content
   */
  useEffect(() => {
    // Populate the field with test data
    if (autoPopulate && inputTestData) {
      setInputValue(inputTestData);
      setIsValid(true);
      setRequiresFormValidation(false);
    }
  }, [autoPopulate]);

  /**
   * Respond to input in the text field
   *
   * @param {Change Event} evt Event that triggered the input to update
   *
   * @returns void
   */
  const onInputChange = (evt) => {
    const fieldValue = evt.target.value;

    if (inputType === 'integer' && fieldValue !== '') {
      // Only allow integer inputs for the "integer" field type
      if (!integerRegEx.test(fieldValue)) {
        return;
      }
    }

    // Check if the input is valid
    const validationSuccess = inputIsValid(fieldValue, inputType);

    // Capture the inputted value
    setInputValue(fieldValue);

    // Field is required, but failed validation
    if (inputRequired && !validationSuccess) {
      // Display the error message
      setIsValid(false);

      // Mark this field as invalid
      setRequiresFormValidation(true);
    }

    if (inputType === 'email') {

      if (fieldValue !== '' && !validationSuccess || fieldValue === '' && inputRequired) {
        // Display the error message
        setIsValid(false);

        // Mark this field as invalid
        setRequiresFormValidation(true);
      } else {
        // Update the validation message
        setIsValid(true);

        // Remove this field from the invalid list
        setRequiresFormValidation(false);
      }
    }

    // Field passed validation
    if (validationSuccess) {
      // Update the validation message
      setIsValid(true);

      // Remove this field from the invalid list
      setRequiresFormValidation(false);
    }
  };

  const onAdditionalButtonClick = (evt) => {
    evt.preventDefault();

    if (!showAdditional) {
      setShowAdditional(true);
    } else {
      setShowAdditional(false);
    }
  };

  /**
   * Validate the field's value
   *
   * @param {String} valueToCheck The value of the input field
   * @param {String} typeOfInput The type of input validation to use. e.g. text, email
   *
   * @returns Boolean
   */
  function inputIsValid(valueToCheck, typeOfInput) {
    let isValid = false;

    switch (typeOfInput) {
      case 'email':
        isValid = validateEmail(valueToCheck);
        break;

      default:
        if (valueToCheck !== '') {
          isValid = true;
        }
        break;
    }

    return isValid;
  }

  /**
   * Generate the input markup and assign the relevant properties
   *
   * @param {String} inputType The input type to create. e.g. 'text' and special types like 'integer'
   * @param {Boolean} hasError Whether or not the form field has failed validation
   *
   * @usage
   *
   * @returns jsx markup
   */
  function generateMarkup(inputType, hasError) {
    const inputProps = {
      id: inputId.current,
      className: hasError ? "qld__text-input qld__text-input--block qld__text-input--error" : "qld__text-input qld__text-input--block",
      name: inputName,
      value: inputValue,
      onChange: onInputChange,
      required: inputRequired,
      autoComplete: "off",
      'data-groupid': groupId,
      'data-validation-label': validationLabel,
      'data-section-label': sectionLabel
    };

    if (inputDescriptionText !== undefined && inputDescriptionText !== null) {
      inputProps['aria-describedby'] = `${inputId.current}-description`;
    }

    switch (inputType) {
      case 'textarea':
        return (
          <textarea
            {...inputProps}
          ></textarea>
        );

      case 'text':
      case 'email':
        return (
          <input
            {...inputProps}
            type={inputType}
          />
        );

      case 'integer':
        return (
          <input
            {...inputProps}
            type="text"
            inputMode="numeric"
          />
        );

      default:
        return (
          <div className="qld__form-group qld__form-style-filled qld__form-group--invalid co-xs-12">
            <span className="qld__label">Alert</span>
            <p className="sq-form-error">Error in component: &quot;FormInput&quot;. Unrecognised input type provided. Type: &quot;{inputType}&quot;</p>
          </div>
        );
    }
  }

  return (
    <div className={parentElementClass}>
      <div>
        <label htmlFor={inputId.current} className="qld__label">{ (inputRequired) ? <abbr title="required">*</abbr> : null } {inputLabel}</label>
        {
          (inputDescriptionText !== undefined && inputDescriptionText !== null) ? <span id={`${inputId.current}-description`} className="qld__hint-text">{inputDescriptionText}</span> : null
        }
        {
          (!isValid) ?
            <p className="sq-form-error">
              {errorMessage}
            </p>
          : null
        }
        { generateMarkup(inputType, !isValid) }
      </div>
      {
        (additionalInputs && !showAdditional) ? <button onClick={onAdditionalButtonClick} className="qld__btn">{additionalInputs.buttonLabel}</button> : null
      }
      {
        (showAdditional) ?
          <div>
            <div>
              <button onClick={onAdditionalButtonClick} className="qld__btn qld__btn--secondary">Remove this section</button>
            </div>
            {
              additionalInputs.inputs.map( (formInput, index) => {
                const formInputConfig = { ...formInput };

                /**
                 * If provided, add the section legend (label) to the input configuration.
                 * This is so we can include the section label in the form validation messages and summary.
                 */
                if (sectionLabel) {
                  formInputConfig.sectionLabel = sectionLabel;
                }

                return (
                  <FormInputWrapper
                    key={index}
                    inputConfiguration={formInputConfig}
                    autoPopulate={autoPopulate}
                    triggerValidation={triggerValidation}
                    formStepValidation={formStepValidation}
                  />
                );
              })
            }
          </div>
        : null
      }
    </div>
  );
};

FormInput.propTypes = {
  "groupId": PropTypes.string,
  "inputType": PropTypes.oneOf(['text', 'email', 'textarea', 'integer']).isRequired,
  "inputLabel": PropTypes.string.isRequired,
  "inputDescriptionText": PropTypes.string,
  "parentElementClass": PropTypes.string,
  "inputName": PropTypes.string.isRequired,
  "inputRequired": PropTypes.bool.isRequired,
  "sectionLabel": PropTypes.string,
  "inputValidationLabel": PropTypes.string,
  "inputValidationMessage": PropTypes.string,
  "additionalInputs": PropTypes.object,
  "inputTestData": PropTypes.string,
  "autoPopulate": PropTypes.bool,
  "triggerValidation": PropTypes.object,
  "formStepValidation": PropTypes.number.isRequired,
  "addValidationField": PropTypes.oneOfType( [PropTypes.object, PropTypes.func] ),
  "removeValidationField": PropTypes.oneOfType( [PropTypes.string, PropTypes.func] )
};

const mapDispatchToProps = (dispatch) => {
  return {
    "addValidationField": formFieldData => dispatch(addValidationField(formFieldData)),
    "removeValidationField": formFieldData => dispatch(removeValidationField(formFieldData))
  };
};

export default connect(null, mapDispatchToProps)(FormInput);